/**
 * Menu Event Handler Hook
 *
 * Listens for Tauri menu events and dispatches them to the appropriate handlers.
 * The Rust menu system emits events when menu items are clicked.
 */

import { useEffect } from 'react'
import type { UnlistenFn } from '@tauri-apps/api/event'
import { useNavigate } from 'react-router-dom'
import { IS_TAURI } from '@/lib/platform'
import { useUIStore } from '@/shared/stores/ui-store'
import { useSettingsStore } from '@/shared/stores/settings-store'

/**
 * Hook to handle menu events from the Rust backend.
 *
 * Should be mounted once at the app level (in GlobalShortcuts or App).
 *
 * Handles:
 *   - menu:navigate - Navigation to different routes
 *   - menu:settings - Opens settings modal (General section)
 *   - menu:developer-tools - Opens settings modal (Developer section)
 *   - menu:toggle-session - Toggles session panel
 *   - menu:zoom - Zoom controls (in/out/reset)
 */
export function useMenuEvents() {
  const navigate = useNavigate()
  const toggleSessionPanel = useUIStore((s) => s.toggleSessionPanel)
  const openSettings = useSettingsStore((s) => s.openSettings)

  useEffect(() => {
    // The native macOS menu bar only exists in the desktop shell. Gating the
    // whole effect (rather than each listener) lets Rollup drop the dynamic
    // import — and the Tauri event module with it — from the browser bundle.
    if (!IS_TAURI) return

    const listeners: UnlistenFn[] = []
    // Set once the effect has torn down, so listeners registered by an
    // in-flight import are unsubscribed instead of leaking.
    let cancelled = false

    const push = (unlisten: UnlistenFn) => {
      if (cancelled) {
        unlisten()
        return
      }
      listeners.push(unlisten)
    }

    void import('@tauri-apps/api/event').then(({ listen }) => {
      if (cancelled) return

      // Handle navigation events
      listen<string>('menu:navigate', (event) => {
        const route = event.payload
        if (route) {
          navigate(route)
        }
      }).then(push)

      // Handle settings menu item — opens the settings modal overlay
      listen('menu:settings', () => {
        openSettings('general')
      }).then(push)

      // Handle developer tools menu item — opens settings on Developer section
      listen('menu:developer-tools', () => {
        openSettings('developer')
      }).then(push)

      // Handle toggle session panel
      listen('menu:toggle-session', () => {
        toggleSessionPanel()
      }).then(push)

      // Handle zoom events
      listen<string>('menu:zoom', (event) => {
        const action = event.payload
        // Zoom is handled by the webview natively for Cmd+/- shortcuts
        // For menu clicks, we can dispatch to the webview
        if (action === 'zoom_in') {
          document.body.style.zoom = String(parseFloat(document.body.style.zoom || '1') * 1.1)
        } else if (action === 'zoom_out') {
          document.body.style.zoom = String(parseFloat(document.body.style.zoom || '1') / 1.1)
        } else if (action === 'zoom_reset') {
          document.body.style.zoom = '1'
        }
      }).then(push)
    })

    // Cleanup
    return () => {
      cancelled = true
      listeners.forEach((unlisten) => unlisten())
    }
  }, [navigate, toggleSessionPanel, openSettings])
}
